import React, { useEffect, useState } from 'react';
import { Chip, EmptyText, Row, Stack, Table, TableColumn, Text, TextButton, Title } from 'library/ui';
import { client, consoleLogInDev, formatPhoneNumber } from 'shared';
import { PagingResult } from 'models';
import { formatMonthDayYearWith12HourTime } from 'shared/Dates';
import AddUser from './AddUser';
import EditUser from './EditUser';
import { useUserContext } from 'UserContext';
import ConfirmDialog from 'reusableComponents/confirmDialog';
import { Colors } from 'library';

interface IBusinessContactListPermission {
    user: number;
    locations: string;
    permissions: string;
    roles: string;
    email: string;
    phone_number: string;
    first_name: string;
    last_name: string;
    last_login: string;
}

type ContactRow = IBusinessContactListPermission & { id: number; hideActions: boolean };

const SPLIT_CHAR = '||';

export default function ManageUsers() {
    const [contacts, setContacts] = useState<PagingResult<IBusinessContactListPermission>>();
    const [pageNumber, setPageNumber] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedContact, setSelectedContact] = useState<IBusinessContactListPermission>();
    const [contactToDelete, setContactToDelete] = useState<IBusinessContactListPermission>();
    const { user } = useUserContext();

    useEffect(() => {
        loadContacts(1);
    }, []);

    function loadContacts(page: number) {
        setIsLoading(true);
        client(`api/business-contact/?page=${page}`)
            .then((res) => setContacts(res))
            .catch(consoleLogInDev)
            .finally(() => setIsLoading(false));
    }

    function onPageChange(newPage: number) {
        setPageNumber(newPage);
        loadContacts(newPage);
    }

    function onContactAdded() {
        loadContacts(pageNumber);
    }

    function onDeleteContact() {
        client(`api/business-contact/delete-contact/`, {
            body: {
                user_id: contactToDelete?.user,
            },
        })
            .then(() => {
                const refreshPage = contacts!.results.length > 1 ? pageNumber : pageNumber - 1;
                loadContacts(refreshPage);
                setPageNumber(refreshPage);
                setContactToDelete(undefined);
            })
            .catch(consoleLogInDev);
    }

    const showCurrentUser = user && user.role !== 'BUSINESS_CONTACT' && pageNumber === 1;
    const tableData: ContactRow[] = [
        ...(showCurrentUser
            ? [
                  {
                      ...user,
                      user: user.id,
                      locations: '',
                      permissions: 'All',
                      roles: '',
                      last_login: '',
                      id: user.id,
                      hideActions: true,
                  } as ContactRow,
              ]
            : []),
        ...(contacts?.results.map((c) => ({ ...c, id: c.user, hideActions: false })) ?? []),
    ];

    const columns: TableColumn<ContactRow>[] = [
        {
            key: 'name',
            label: 'Name (Last Login)',
            render: (row) => (
                <Stack gap="none">
                    <Text size="sm">
                        {row.first_name} {row.last_name}
                    </Text>
                    <Text size="xs" tone="secondary">
                        {row.last_login ? formatMonthDayYearWith12HourTime(row.last_login) : ''}
                    </Text>
                </Stack>
            ),
        },
        {
            key: 'contact',
            label: 'Contact Info',
            render: (row) => (
                <Stack gap="none">
                    <Text size="sm">{row.email}</Text>
                    <Text size="sm">{row.phone_number ? formatPhoneNumber(row.phone_number) : ''}</Text>
                </Stack>
            ),
        },
        {
            key: 'access',
            label: 'Access',
            render: (row) =>
                row.locations ? (
                    <Stack gap="none">
                        {row.locations.split(SPLIT_CHAR).map((s, index) => (
                            <Text key={index} size="sm">
                                {s}
                            </Text>
                        ))}
                    </Stack>
                ) : (
                    <Text size="sm">All Locations</Text>
                ),
        },
        {
            key: 'permissions',
            label: 'Permissions',
            render: (row) =>
                row.permissions ? (
                    <Row gap="sm" wrap>
                        {row.permissions.split(SPLIT_CHAR).map((x) => (
                            <Chip bgColor={Colors.secondarySoft} textColor={Colors.primary} variant="filled" key={x}>
                                {x}
                            </Chip>
                        ))}
                    </Row>
                ) : null,
        },
        {
            key: 'roles',
            label: 'Roles',
            render: (row) =>
                row.roles ? (
                    <Row gap="sm" wrap>
                        {row.roles.split(SPLIT_CHAR).map((x) => (
                            <Chip bgColor={Colors.accentSoft} textColor={Colors.primary} variant="filled" key={x}>
                                {x}
                            </Chip>
                        ))}
                    </Row>
                ) : null,
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (row) =>
                row.hideActions ? null : (
                    <Row gap="sm" align="center">
                        <TextButton onClick={() => setSelectedContact(row)}>Edit</TextButton>
                        <Text tone="secondary">|</Text>
                        <TextButton onClick={() => setContactToDelete(row)}>Delete</TextButton>
                    </Row>
                ),
        },
    ];

    return (
        <Stack gap="md">
            <Row justify="between" align="center">
                <Title size="h1">Manage Users</Title>
                <Row gap="md" align="center">
                    {isLoading && <EmptyText>Loading...</EmptyText>}
                    <AddUser onContactAdded={onContactAdded} />
                </Row>
            </Row>
            <Table
                columns={columns}
                data={tableData}
                page={pageNumber}
                count={contacts?.count ?? 0}
                perPage={contacts?.per_page ?? 25}
                onPageChange={onPageChange}
            />
            <EditUser
                isModalOpen={Boolean(selectedContact)}
                onClose={() => setSelectedContact(undefined)}
                userId={selectedContact?.user}
                initialLocations={selectedContact?.locations ? selectedContact?.locations.split(SPLIT_CHAR) : []}
                initialPermissions={selectedContact?.permissions.split(SPLIT_CHAR)}
                initialRoles={selectedContact?.roles.split(SPLIT_CHAR)}
                onUserUpdated={() => {
                    setSelectedContact(undefined);
                    loadContacts(pageNumber);
                }}
                name={`${selectedContact?.first_name} ${selectedContact?.last_name}`}
            />
            <ConfirmDialog
                title="Confirm Delete"
                message="Are you sure you want to delete this contact?"
                open={Boolean(contactToDelete)}
                onClose={() => setContactToDelete(undefined)}
                onConfirm={onDeleteContact}
            />
        </Stack>
    );
}
